import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import * as api from "../services/api";

const engagementPhases = [
  {
    num: "01",
    name: "Assess",
    color: "bg-indigo-600",
    tagline: "Clarity before action",
    duration: "1–2 weeks",
    deliverables: [
      "Current-state workflow audit",
      "Tool stack gap analysis",
      "Priority matrix: quick wins vs. strategic investments",
      "Written findings report with specific recommendations",
    ],
    desc: "We start by understanding your existing workflows, pain points, and goals — not by proposing solutions. You get a documented, prioritized view of exactly what needs to change.",
  },
  {
    num: "02",
    name: "Build",
    color: "bg-purple-600",
    tagline: "Structured execution",
    duration: "4–12 weeks",
    deliverables: [
      "Custom tool configuration or bespoke development",
      "Integration with your existing systems",
      "Documentation and runbooks",
      "Team training and onboarding sessions",
    ],
    desc: "Hands-on implementation phase. We configure, build, and integrate — following the prioritized roadmap from the Assess phase. Weekly check-ins keep everyone aligned.",
  },
  {
    num: "03",
    name: "Scale",
    color: "bg-emerald-600",
    tagline: "Sustained momentum",
    duration: "Ongoing, monthly",
    deliverables: [
      "Monthly advisory call (strategy + tool reviews)",
      "Priority support access",
      "Quarterly workflow health check",
      "Access to new tools as they launch",
    ],
    desc: "After delivery, we offer a structured advisory relationship to help your team continuously improve. Not a retainer you forget — a real monthly touchpoint with measurable value.",
  },
];

const outcomes = [
  {
    industry: "E-commerce & Retail",
    result: "Budget reporting dramatically simplified",
    context: "A DTC brand replaced disconnected spreadsheet workflows with our Budget Planner during an Assess + Build engagement. Monthly close moved from a multi-day process to a single focused session.",
    tag: "Finance Workflow",
  },
  {
    industry: "Marketing Agency",
    result: "Proposal process made repeatable",
    context: "A small creative agency used our consulting services to standardize their client intake, scoping, and financial estimation. Proposals became consistent, faster, and easier to delegate.",
    tag: "Operational Efficiency",
  },
  {
    industry: "Independent Financial Advisor",
    result: "Client review prep made systematic",
    context: "We helped an independent advisor configure our Portfolio Risk Analyzer with reusable client presets. Pre-meeting analysis became a defined, repeatable workflow instead of ad-hoc research.",
    tag: "Financial Services",
  },
];

const industries = [
  { icon: "💼", name: "Financial Services", detail: "Budgeting, forecasting, portfolio analysis" },
  { icon: "🛍️", name: "Retail & E-commerce", detail: "Revenue modeling, margin tracking, campaign tools" },
  { icon: "🏥", name: "Healthcare & Wellness", detail: "Operational workflows, compliance documentation" },
  { icon: "🎨", name: "Creative & Marketing", detail: "Brand tools, content pipelines, proposal automation" },
  { icon: "💻", name: "Technology & SaaS", detail: "Developer tooling, analytics, API integration" },
  { icon: "🏗️", name: "Professional Services", detail: "Project scoping, client reporting, time analysis" },
];

const partners = [
  { name: "Cloudflare", role: "Edge Compute & Security" },
  { name: "Stripe", role: "Payments & Billing" },
  { name: "React", role: "Frontend Framework" },
  { name: "TypeScript", role: "Type-Safe Development" },
];

const expertise = [
  {
    category: "Cloud Platforms",
    icon: "☁️",
    items: [
      { name: "Amazon Web Services (AWS)", detail: "EC2, S3, Lambda, RDS, CloudFront" },
      { name: "Google Cloud Platform", detail: "GKE, Cloud Run, BigQuery, Firebase" },
      { name: "Microsoft Azure", detail: "App Service, Azure DevOps, Blob Storage" },
      { name: "Cloudflare", detail: "Workers, Pages, D1, R2, Zero Trust" },
    ],
  },
  {
    category: "Content Management Systems",
    icon: "📝",
    items: [
      { name: "WordPress", detail: "Custom themes, plugins, headless CMS" },
      { name: "Adobe Experience Manager (AEM)", detail: "Sites, Assets, Forms, Edge Delivery" },
      { name: "Adobe Edge Delivery Services", detail: "High-performance document-driven sites" },
      { name: "Contentful / Sanity", detail: "API-first headless CMS architecture" },
    ],
  },
  {
    category: "Frontend Technologies",
    icon: "⚛️",
    items: [
      { name: "Next.js", detail: "SSR, SSG, App Router, API routes" },
      { name: "React", detail: "Component systems, state management, hooks" },
      { name: "Vite", detail: "Fast dev tooling, SPA and library builds" },
      { name: "TypeScript", detail: "Type-safe codebases across frontend & backend" },
    ],
  },
  {
    category: "Analytics & Optimization",
    icon: "📊",
    items: [
      { name: "Google Analytics 4", detail: "Event tracking, funnel analysis, audiences" },
      { name: "Google Tag Manager", detail: "Tag governance, trigger management, datalayer" },
      { name: "Looker Studio", detail: "Custom dashboards and live reporting" },
      { name: "Adobe Analytics", detail: "Enterprise event tracking and segmentation" },
    ],
  },
  {
    category: "Automation & Integrations",
    icon: "⚙️",
    items: [
      { name: "Zapier", detail: "No-code workflow automation across 6,000+ apps" },
      { name: "Make (Integromat)", detail: "Complex multi-step automation scenarios" },
      { name: "n8n", detail: "Self-hosted workflow orchestration" },
      { name: "REST / GraphQL APIs", detail: "Custom integration architecture and middleware" },
    ],
  },
  {
    category: "Project & Team Collaboration",
    icon: "🗂️",
    items: [
      { name: "Jira", detail: "Sprint planning, backlog management, boards" },
      { name: "Confluence", detail: "Documentation, SOPs, decision logs" },
      { name: "Bitbucket", detail: "Git repos, Pipelines CI/CD, pull requests" },
      { name: "Trello", detail: "Lightweight kanban for small teams" },
    ],
  },
];

export default function Consulting() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "", engagement: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [location.hash]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    try {
      await api.submitContact(form.name, form.email, form.message);
      setSent(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const inputClass = "w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-sm";

  return (
    <div className="min-h-screen bg-white">

      {/* ── Hero ── */}
      <section className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-indigo-300 font-semibold uppercase tracking-widest text-sm mb-5">Consulting Services</p>
          <h1 className="text-5xl font-extrabold leading-tight mb-6">
            You have the vision.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 to-purple-300">
              We help you execute.
            </span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed mb-10">
            Boutique agility meets enterprise-quality thinking. Whether you need a 2-week audit or a 12-week implementation, we bring structured methodology to every engagement — regardless of your team's size.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#contact" className="inline-block bg-white text-indigo-700 font-bold px-8 py-4 rounded-xl hover:bg-indigo-50 transition shadow-md">
              Start a Conversation →
            </a>
            <a href="#engagement" className="inline-block border border-white/30 text-white font-semibold px-8 py-4 rounded-xl hover:border-white/60 transition">
              Our Engagement Model
            </a>
          </div>
        </div>
      </section>

      {/* ── Problem statement ── */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {[
              { icon: "😤", headline: "Too many tools, too little clarity", desc: "Your team uses a dozen apps and none of them talk to each other. Reporting takes days. Decisions lag." },
              { icon: "📉", headline: "Processes that don't scale", desc: "What worked at 5 people breaks at 15. Workflows built on habits instead of systems hold growth back." },
              { icon: "⏳", headline: "Implementation always gets deprioritized", desc: "You know what needs to change. The work to get there keeps slipping to next quarter." },
            ].map((p) => (
              <div key={p.headline} className="p-6 rounded-2xl border border-gray-100 bg-gray-50">
                <div className="text-3xl mb-3">{p.icon}</div>
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{p.headline}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-500 mt-8 text-sm">
            <span className="font-semibold text-gray-800">We've seen these patterns across every industry.</span>{" "}
            Our engagement model is designed specifically to address them.
          </p>
        </div>
      </section>

      {/* ── Engagement Model ── */}
      <section className="py-20 bg-white" id="engagement">
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-indigo-600 font-semibold uppercase tracking-widest text-sm text-center mb-4">Our Methodology</p>
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">The Assess → Build → Scale Framework</h2>
          <p className="text-gray-500 text-center max-w-xl mx-auto mb-14">
            Every engagement follows a three-phase structure. You decide where to start and when to stop.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {engagementPhases.map((phase) => (
              <div key={phase.name} className="rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition">
                <div className={`${phase.color} p-6 text-white`}>
                  <span className="text-xs font-bold uppercase tracking-widest opacity-70">{phase.num}</span>
                  <h3 className="text-2xl font-extrabold mt-1">{phase.name}</h3>
                  <p className="text-sm opacity-80 mt-1 font-medium">{phase.tagline}</p>
                  <div className="mt-4 bg-white/15 rounded-lg px-3 py-1.5 inline-block text-xs font-semibold">
                    {phase.duration}
                  </div>
                </div>
                <div className="p-6 bg-white">
                  <p className="text-gray-600 text-sm leading-relaxed mb-5">{phase.desc}</p>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Deliverables</p>
                  <ul className="space-y-2">
                    {phase.deliverables.map((d, i) => (
                      <li key={i} className="flex gap-2 text-xs text-gray-600">
                        <span className="text-indigo-400 shrink-0">→</span>
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-indigo-600 font-semibold uppercase tracking-widest text-sm text-center mb-4">Industries Served</p>
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">We speak your domain</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {industries.map((ind) => (
              <div key={ind.name} className="flex gap-4 p-5 bg-white rounded-2xl border border-gray-100 shadow-sm hover:border-indigo-200 transition">
                <span className="text-2xl shrink-0">{ind.icon}</span>
                <div>
                  <p className="font-bold text-gray-900 text-sm">{ind.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{ind.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Outcomes ── */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-indigo-600 font-semibold uppercase tracking-widest text-sm text-center mb-4">Client Outcomes</p>
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">Results that matter</h2>
          <p className="text-gray-500 text-center max-w-lg mx-auto mb-12">
            We measure every engagement by outcomes, not deliverable counts. Here are representative results from recent engagements.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {outcomes.map((o) => (
              <div key={o.industry} className="rounded-2xl border border-gray-100 p-7 shadow-sm hover:shadow-md transition bg-white">
                <span className="inline-block text-xs font-bold bg-indigo-50 text-indigo-600 px-2.5 py-1 rounded-full mb-4">{o.tag}</span>
                <p className="text-2xl font-extrabold text-gray-900 mb-3 leading-tight">{o.result}</p>
                <p className="text-xs text-indigo-600 font-semibold uppercase tracking-wide mb-2">{o.industry}</p>
                <p className="text-gray-500 text-xs leading-relaxed">{o.context}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Areas of Expertise ── */}
      <section id="expertise" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <p className="text-indigo-600 font-semibold uppercase tracking-widest text-sm text-center mb-4">Areas of Expertise</p>
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">Technology depth across the full stack</h2>
          <p className="text-gray-500 text-center max-w-xl mx-auto mb-14">
            We work across platforms, not in silos. Whether you're migrating a CMS, instrumenting analytics, or automating workflows — we've done it before.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {expertise.map((group) => (
              <div key={group.category} className="border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition bg-white">
                <div className="flex items-center gap-2 mb-5">
                  <span className="text-2xl">{group.icon}</span>
                  <h3 className="font-bold text-gray-900 text-sm">{group.category}</h3>
                </div>
                <ul className="space-y-3">
                  {group.items.map((item) => (
                    <li key={item.name}>
                      <p className="text-sm font-semibold text-gray-800">{item.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{item.detail}</p>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Partner ecosystem ── */}
      <section className="py-12 bg-gray-50 border-y border-gray-100">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-6">Our work is built on trusted technology partners</p>
          <div className="flex flex-wrap justify-center gap-6">
            {partners.map((p) => (
              <div key={p.name} className="flex flex-col items-center px-7 py-4 bg-white rounded-xl border border-gray-200 shadow-sm min-w-[130px]">
                <p className="font-bold text-gray-800 text-sm">{p.name}</p>
                <p className="text-xs text-gray-400 mt-0.5">{p.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why us ── */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-indigo-600 font-semibold uppercase tracking-widest text-sm mb-4">Why 9nd.org Consulting</p>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Boutique team. Enterprise-grade approach.</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Large consulting firms bring methodology and overhead in equal measure. With us, you get structured thinking without the overhead: direct access to the people doing the work, faster turnaround, and no layers between your problem and our solution.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Because we build and maintain the tools ourselves, our consulting practice has an unfair advantage: we know the edge cases, the configuration levers, and the integration patterns that generic consultants don't.
            </p>
          </div>
          <div className="space-y-4">
            {[
              { icon: "👥", title: "Direct access to your team", desc: "No account managers or project coordinators as intermediaries." },
              { icon: "🏎️", title: "Faster execution cycles", desc: "Typical Assess phase completed in 5–7 business days, not 6 weeks." },
              { icon: "🔧", title: "Tool-native expertise", desc: "We built the tools. We know exactly how to configure them for your use case." },
              { icon: "📊", title: "Outcome-based accountability", desc: "We define measurable success criteria in writing before every engagement." },
            ].map((item) => (
              <div key={item.title} className="flex gap-4 p-4 border border-gray-100 rounded-xl hover:border-indigo-200 transition">
                <span className="text-xl shrink-0">{item.icon}</span>
                <div>
                  <p className="font-bold text-gray-900 text-sm">{item.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Contact ── */}
      <section className="bg-gray-50 py-20 border-t border-gray-100" id="contact">
        <div className="max-w-2xl mx-auto px-4">
          <p className="text-indigo-600 font-semibold uppercase tracking-widest text-sm text-center mb-4">Start a Conversation</p>
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-3">Let's talk about your goals</h2>
          <p className="text-gray-500 text-center mb-10 text-sm">
            Most engagements start with a free 30-minute scoping call. No pressure, no pitch deck — just a real conversation about what you're trying to solve.
          </p>
          {sent ? (
            <div className="bg-green-50 border border-green-200 text-green-700 rounded-2xl p-8 text-center shadow-sm">
              <div className="text-3xl mb-3">✅</div>
              <p className="font-bold text-lg mb-1">Message received</p>
              <p className="text-sm">We typically respond within one business day. We'll reach out to schedule a scoping call.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 bg-white border border-gray-100 rounded-2xl p-8 shadow-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input type="text" placeholder="Your Name" required className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input type="email" placeholder="Work Email" required className={inputClass} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <input type="text" placeholder="Company or Organization" className={inputClass} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
              <select
                className={inputClass + " bg-white"}
                value={form.engagement}
                onChange={(e) => setForm({ ...form, engagement: e.target.value })}
              >
                <option value="">Engagement type (optional)</option>
                <option value="assess">Assess — Audit & recommendations</option>
                <option value="build">Build — Implementation project</option>
                <option value="scale">Scale — Ongoing advisory</option>
                <option value="custom">Custom / not sure yet</option>
              </select>
              <textarea
                placeholder="Tell us what you're working on — the more detail, the better our first conversation will be."
                required
                rows={5}
                className={inputClass}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
              />
              {error && <p className="text-red-600 text-sm">{error}</p>}
              <button
                type="submit"
                disabled={sending}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3.5 rounded-xl font-bold hover:opacity-90 disabled:opacity-50 transition shadow-md"
              >
                {sending ? "Sending..." : "Send Message — Let's Talk"}
              </button>
              <p className="text-center text-xs text-gray-400">
                We respond within 1 business day.{" "}
                <Link to="/trust" className="underline underline-offset-1 hover:text-indigo-600">Privacy policy →</Link>
              </p>
            </form>
          )}
        </div>
      </section>

    </div>
  );
}